/**
 * Circuit breaker pattern for fault tolerance
 *
 * States:
 * - closed: Normal operation, requests pass through
 * - open: Failures exceeded threshold, requests fail fast
 * - half-open: Testing if service has recovered
 */

import { CircuitBreakerOpenError } from './errors';

export type CircuitState = 'closed' | 'open' | 'half-open';

export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit */
  threshold: number;
  /** Time in ms before attempting to close circuit */
  resetTimeout: number;
  /** Optional name for logging */
  name?: string;
}

const DEFAULT_CONFIG: CircuitBreakerConfig = {
  threshold: 5,
  resetTimeout: 30000,
};

export class CircuitBreaker {
  private failures = 0;
  private lastFailure = 0;
  private state: CircuitState = 'closed';
  private config: CircuitBreakerConfig;

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Execute a function with circuit breaker protection.
   * `shouldCount` lets the caller exclude expected/transient errors (e.g. rate
   * limits) from tripping the breaker — those aren't signals of provider unhealth.
   */
  async execute<T>(fn: () => Promise<T>, opts?: { shouldCount?: (err: unknown) => boolean }): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailure > this.config.resetTimeout) {
        // Try to recover
        this.state = 'half-open';
      } else {
        throw new CircuitBreakerOpenError(this.config.name || 'service');
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      if (!opts?.shouldCount || opts.shouldCount(error)) {
        this.onFailure();
      }
      throw error;
    }
  }

  /**
   * Record a successful operation
   */
  private onSuccess(): void {
    this.failures = 0;
    this.state = 'closed';
  }

  /**
   * Record a failed operation
   */
  private onFailure(): void {
    this.failures++;
    this.lastFailure = Date.now();
    if (this.failures >= this.config.threshold) {
      this.state = 'open';
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    return this.state;
  }

  /**
   * Get current failure count
   */
  getFailures(): number {
    return this.failures;
  }

  /**
   * Manually reset the circuit breaker
   */
  reset(): void {
    this.failures = 0;
    this.lastFailure = 0;
    this.state = 'closed';
  }
}

/**
 * Factory function to create circuit breakers for multiple services
 */
export function createCircuitBreakers<T extends string>(
  services: T[],
  config: Partial<CircuitBreakerConfig> = {}
): Record<T, CircuitBreaker> {
  const breakers: Record<string, CircuitBreaker> = {};
  for (const service of services) {
    breakers[service] = new CircuitBreaker({ ...config, name: service });
  }
  return breakers as Record<T, CircuitBreaker>;
}
